import './TimelineInteractions.scss';
import dayjs from 'dayjs';
import {
  ComposedChart,
  FilterDropdown,
  Grid,
  GridItem,
  LineChart,
} from 'plume-ui';
import { ComposedChartDataItem } from 'plume-ui/dist/utils/charts/chartTypes';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  SHORT_DATE_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  STANDARD_DATE_FORMAT,
  STANDARD_TIME_FORMAT,
} from '../../../../config';
import { MixPanelEvents } from '../../../../mixPanelEvents';
// Data providers
import { activePartnerAtom } from '../../../../state';
import { Maybe } from '../../../../types';
// Mocks
import {
  getDurationItems,
  mapChartDurationToTicks,
} from '../../../../utils/AlarmUtils';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
import {
  AdjustableComponent,
  useClasses,
} from '../../../../utils/hooks/useClasses';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { getLegendItems } from '../../containers/fixtures';
import { HealthSummaryGenericStyles } from '../../containers/HealthSummary';
import { tickFormatter } from '../../healthChartUtils';
//Renderers and formatters

import useCriteriaNames from '../../../Monitor/hooks/useCriteriaNames';
import { AlarmLegend } from '../../renderers';
import {
  useGetPartnerHealthScoreTimeline,
  useGetPartnerIssues,
  useGetTypicalHealthScoreTimeline,
  useGetTypicalIssues,
} from '../../services/healthcheckServices';
import {
  getIssueStackGroup,
  getTimelineGroups,
  procStacks,
  procTimeline,
  StackDataType,
  TimelineChartDataType,
} from '../../services/timelineProc';
import ChartLegend from '../ChartLegend/ChartLegend';

type TimelineInteractionsProps = {
  passedClasses: HealthSummaryGenericStyles;
};

type TimelineInteractionsStyles = {} & HealthSummaryGenericStyles;

const TimelineInteractions: AdjustableComponent<
  TimelineInteractionsProps,
  TimelineInteractionsStyles
> = ({ classes, passedClasses }) => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const { getCriteriaName } = useCriteriaNames();
  const [dateSince, setDateSince] = useState<{ label: string; value: string }>(
    getDurationItems(t)[2],
  );
  const activePartner = useRecoilValue(activePartnerAtom);
  const [durationLabel, setDurationLabel] = useState(dateSince.label);

  const {
    partnerHealthScoreTimelineLoading,
    partnerHealthScoreTimelineDataArray,
  } = useGetPartnerHealthScoreTimeline(
    activePartner?.id ? activePartner.id : '',
    dateSince.value,
  );
  const {
    typicalHealthScoreTimelineLoading,
    typicalHealthScoreTimelineDataArray,
  } = useGetTypicalHealthScoreTimeline(dateSince.value);
  const [timelineData, setTimelineData] = useState<TimelineChartDataType[]>([]);

  useEffect(() => {
    if (
      !partnerHealthScoreTimelineLoading &&
      !typicalHealthScoreTimelineLoading
    ) {
      if (
        partnerHealthScoreTimelineDataArray?.length &&
        typicalHealthScoreTimelineDataArray?.length
      )
        setTimelineData(
          procTimeline(
            typicalHealthScoreTimelineDataArray,
            partnerHealthScoreTimelineDataArray,
            (timestamp: string) => {
              return dateSince.value === '1'
                ? dayjs.utc(timestamp).local().format(STANDARD_TIME_FORMAT)
                : dayjs.utc(timestamp).local().format(SHORT_DATE_FORMAT);
            },
          ),
        );
      else setTimelineData([]);
    }
  }, [
    partnerHealthScoreTimelineDataArray,
    typicalHealthScoreTimelineDataArray,
    partnerHealthScoreTimelineLoading,
    typicalHealthScoreTimelineLoading,
  ]);

  const { typicalIssuesLoading, typicalIssuesData } = useGetTypicalIssues(
    dateSince.value,
    false,
  );
  const { partnerIssuesLoading, partnerIssuesData } = useGetPartnerIssues(
    activePartner?.id ? activePartner.id : '',
    dateSince.value,
    false,
  );

  const {
    typicalIssuesLoading: latestTypicalIssuesLoading,
    typicalIssuesData: latestTypicalIssuesData,
  } = useGetTypicalIssues();
  const {
    partnerIssuesLoading: latestPartnerIssuesLoading,
    partnerIssuesData: latestPartnerIssuesData,
  } = useGetPartnerIssues(activePartner?.id ? activePartner.id : '');
  const [issueStacks, setIssueStacks] = useState<StackDataType[]>([]);
  const [selectedDate, setSelectedDate] = useState<Maybe<string>>();
  const [volumeChartData, setVolumeChartData] = useState<
    Maybe<ComposedChartDataItem[]>
  >();
  const [timelineChartData, setTimelineChartData] = useState<
    Maybe<TimelineChartDataType[]>
  >();

  const [breakdownDate, setBreakdownDate] = useState<string>(
    dayjs()
      .subtract(dayjs().minute() % 15, 'minute')
      .format(STANDARD_DATE_FORMAT),
  );

  useEffect(() => {
    setTimelineChartData(renderTimelineData(timelineData));
  }, [timelineData]);

  useEffect(() => {
    setVolumeChartData(renderBarchartData(issueStacks));
  }, [issueStacks]);

  useEffect(() => {
    if (
      !typicalIssuesLoading &&
      typicalIssuesData &&
      !partnerIssuesLoading &&
      partnerIssuesData &&
      !latestTypicalIssuesLoading &&
      latestTypicalIssuesData &&
      !latestPartnerIssuesLoading &&
      latestPartnerIssuesData
    ) {
      const typicalIndex = typicalIssuesData
        .map((item) =>
          dayjs
            .utc(item.ts)
            .local()
            .format(
              dateSince.value === '1'
                ? STANDARD_TIME_FORMAT
                : SHORT_DATE_FORMAT,
            ),
        )
        .findIndex((item) => item === selectedDate);
      const partnerIndex = partnerIssuesData
        .map((item) =>
          dayjs
            .utc(item.ts)
            .local()
            .format(
              dateSince.value === '1'
                ? STANDARD_TIME_FORMAT
                : SHORT_DATE_FORMAT,
            ),
        )
        .findIndex((item) => item === selectedDate);
      if (
        selectedDate !== undefined &&
        typicalIndex === partnerIndex &&
        typicalIndex !== undefined &&
        partnerIssuesData[partnerIndex] !== undefined
      ) {
        setBreakdownDate(selectedDate);
        setIssueStacks(
          procStacks(
            {
              ...typicalIssuesData[typicalIndex],
              location_count: partnerIssuesData[partnerIndex]?.location_count,
            },
            partnerIssuesData[partnerIndex],
          ),
        );
      }
      if (typicalIndex !== partnerIndex || selectedDate === undefined) {
        setBreakdownDate(
          dayjs()
            .subtract(dayjs().minute() % 15, 'minute')
            .format(SHORT_DATE_TIME_FORMAT),
        );
        setIssueStacks(
          procStacks(
            {
              ...latestTypicalIssuesData[0],
              location_count: latestPartnerIssuesData[0]?.location_count || 0,
            },
            latestPartnerIssuesData[0],
          ),
        );
      }
    }
  }, [
    typicalIssuesData,
    partnerIssuesData,
    latestTypicalIssuesData,
    latestPartnerIssuesData,
    selectedDate,
  ]);

  useEffect(() => {
    setSelectedDate(undefined);
    setBreakdownDate(
      dayjs()
        .subtract(dayjs().minute() % 15, 'minute')
        .format(SHORT_DATE_TIME_FORMAT),
    );
  }, [dateSince.value]);

  const styles = useClasses(
    {
      root: `TimelineInteractions`,
      title: `${passedClasses.title} TimelineInteractions__${
        passedClasses.title.split('HealthSummary__')[1]
      }`,
      menuRow: `${passedClasses.menuRow} TimelineInteractions__${
        passedClasses.menuRow.split('HealthSummary__')[1]
      }`,
      summaryCell: `${passedClasses.summaryCell} TimelineInteractions__${
        passedClasses.summaryCell.split('HealthSummary__')[1]
      }`,
    },
    classes,
  );

  const renderTimelineData = (
    tlData: TimelineChartDataType[],
  ): Maybe<TimelineChartDataType[]> =>
    tlData.length === 0
      ? undefined
      : tlData.map((entry) => {
          return {
            ...entry,
            partnerScore: parseFloat(entry.partnerScore.toFixed(2)),
            typicalScore: parseFloat(entry.typicalScore.toFixed(2)),
          };
        });

  const renderBarchartData = (
    bchData: StackDataType[],
  ): Maybe<StackDataType[]> => {
    let empty = true;
    const res = bchData.map((stack) => {
      return {
        ...stack,
        values: stack.values.map((value) => {
          if (value !== 0) empty = false;
          return parseFloat(value.toFixed(2));
        }),
      };
    });
    return empty ? undefined : res;
  };

  const issueStackGroups = useMemo(() => getIssueStackGroup(getCriteriaName), [
    getCriteriaName,
  ]);

  return (
    <div className={styles.root}>
      <Grid>
        <GridItem colSpan="12" tabletColSpan="12" className={styles.menuRow}>
          <FilterDropdown
            defaultLabel={durationLabel || ''}
            containerLabel={t('genericLabels.timeRange')}
            onSelect={(item) => {
              setDateSince(item);
              setDurationLabel(item.label);
              trackEvent({
                eventName: MixPanelEvents.HEALTH_SUMMARY_TIME_RANGE,
                additionalContent: {
                  TIME_RANGE: item.label,
                },
              });
            }}
            closeOnItemClick={true}
            items={getDurationItems(t)}
          />
        </GridItem>

        <GridItem colSpan="6" tabletColSpan="12">
          <div className={styles.summaryCell}>
            <div className={styles.title}>
              <FormattedMessage id="healthSummary.timelineInteractions.avgNetworkHealth.label" />
            </div>
            <LineChart
              curveType={'linear'}
              height={230}
              data={timelineChartData}
              groups={getTimelineGroups()}
              tickFormatter={tickFormatter}
              xAxisTickInterval={mapChartDurationToTicks(
                timelineChartData?.length!,
              )}
              showLastElementOnXAxis={false}
              showFirstElementOnXAxis={false}
              domainY={[0, 100]}
              onChartClick={(item) => {
                setSelectedDate(item.activeLabel);
                trackEvent({
                  eventName: MixPanelEvents.HEALTH_SUMMARY_ISSUE_DATE,
                  additionalContent: {
                    ISSUE_DATE: item.activeLabel || 'undefined date',
                  },
                });
              }}
              statusVariant="standard"
              legendPosition="none"
            />
            {timelineChartData !== undefined && (
              <ChartLegend {...getLegendItems()} />
            )}
          </div>
        </GridItem>

        <GridItem colSpan="6" tabletColSpan="12">
          <div className={styles.summaryCell}>
            <div className={styles.title}>
              <FormattedMessage
                id="healthSummary.timelineInteractions.breakdownByCategory.label"
                params={{
                  timeframe: breakdownDate,
                }}
              />
            </div>
            {!!issueStacks.length && (
              <ComposedChart
                data={volumeChartData}
                invertHoverEffect={true}
                config={{
                  height: 210,
                  showPercentageOnTooltip: false,
                  showTooltipTitleForNumerical: true,
                  showVerticalCartesianGrid: true,
                  xAxisLabelAngle: 0,
                }}
                groups={issueStackGroups}
                stacked
              />
            )}
            {volumeChartData !== undefined && <AlarmLegend />}
          </div>
        </GridItem>
      </Grid>
    </div>
  );
};

export default memo(TimelineInteractions);
